/**
 * @fileoverview Multi-Entity Cards - Read-only cards for coverage and provider imports.
 *
 * Renders actual imported data as cards instead of opaque "N items" summaries.
 * Used by the assist panel when a field is multi-entity (array of records).
 */
import React from 'react';

import type {
  ImportedCoverageEntry,
  ImportedProvider,
} from '@/services/types/case-ingestion-assist-types';

import { CopyButton } from './CopyButton';
import type { AssistFieldRow } from './types';

// =============================================================================
// Copyable Value - Inline label + value with hover-reveal copy button
// =============================================================================

const CopyableValue: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="group flex items-center justify-between gap-1">
    <p className="text-[10px] font-medium text-zinc-500">
      {label}: {value}
    </p>
    <CopyButton value={value} />
  </div>
);

// =============================================================================
// Coverage Card
// =============================================================================

const CoverageCard: React.FC<{ entry: ImportedCoverageEntry; index: number }> = ({
  entry,
  index,
}) => (
  <div className="rounded-md border border-zinc-100 bg-zinc-50 px-2.5 py-2 dark:border-zinc-800 dark:bg-zinc-800/50">
    <div className="group flex items-center justify-between gap-1">
      <p className="text-[11px] font-semibold text-zinc-700 dark:text-zinc-300">
        {entry.carrier_name || `Coverage ${index + 1}`}
      </p>
      {entry.carrier_name && <CopyButton value={entry.carrier_name} />}
    </div>
    {entry.policy_number && (
      <CopyableValue label="Policy" value={entry.policy_number} />
    )}
    {entry.claim_number && (
      <CopyableValue label="Claim" value={entry.claim_number} />
    )}
    {entry.adjuster_name && (
      <CopyableValue label="Adjuster" value={entry.adjuster_name} />
    )}
    {entry.adjuster_phone && (
      <CopyableValue label="Adjuster Phone" value={entry.adjuster_phone} />
    )}
  </div>
);

// =============================================================================
// Provider Card
// =============================================================================

const ProviderCard: React.FC<{ provider: ImportedProvider; index: number }> = ({
  provider,
  index,
}) => (
  <div className="rounded-md border border-zinc-100 bg-zinc-50 px-2.5 py-2 dark:border-zinc-800 dark:bg-zinc-800/50">
    <div className="group flex items-center justify-between gap-1">
      <p className="text-[11px] font-semibold text-zinc-700 dark:text-zinc-300">
        {provider.name || `Provider ${index + 1}`}
      </p>
      {provider.name && <CopyButton value={provider.name} />}
    </div>
    {provider.type && (
      <CopyableValue label="Type" value={provider.type} />
    )}
    {provider.phone && (
      <CopyableValue label="Phone" value={provider.phone} />
    )}
    {provider.address && (
      <CopyableValue label="Address" value={provider.address} />
    )}
  </div>
);

// =============================================================================
// Multi-Entity Field Row
// =============================================================================

/**
 * Renders a multi-entity field as a label + count + stack of cards.
 * Detects whether items are coverage entries or providers based on field ID.
 */
export const MultiEntityFieldRow: React.FC<{ row: AssistFieldRow }> = ({ row }) => {
  const items = row.importedValue;
  if (!Array.isArray(items) || items.length === 0) {
    return (
      <p className="text-[10px] font-medium text-zinc-500">No items imported</p>
    );
  }

  const isCoverage = row.fieldId.startsWith('coverage.');

  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-medium text-zinc-500">
        {items.length} {items.length === 1 ? 'item' : 'items'} imported
      </p>
      <div className="space-y-1">
        {isCoverage
          ? (items as ImportedCoverageEntry[]).map((entry, i) => (
              <CoverageCard key={i} entry={entry} index={i} />
            ))
          : (items as ImportedProvider[]).map((provider, i) => (
              <ProviderCard key={i} provider={provider} index={i} />
            ))}
      </div>
    </div>
  );
};
